import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/translations';
import { useLanguage } from '../../context/LanguageContext';
import RecordingModal from '../ui/RecordingModal';

const MAX_WORDS = 100;
const MAX_RECORDING_TIME = 30;

const FreeTextInput = ({
  value,
  onChange,
  onSubmit,
  onFocus = null,
  onBlur = null,
  placeholder = '',
  className = '',
  style = {},
  disabled = false,
}) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [wordCount, setWordCount] = useState(0);
  const [showMicrophone, setShowMicrophone] = useState(true);
  const [isVoiceText, setIsVoiceText] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showClearButton, setShowClearButton] = useState(false);
  const inputRef = useRef(null);
  const updateTextareaLayout = (textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const maxHeight = 80;
    if (scrollHeight > maxHeight) {
      textarea.style.height = maxHeight + 'px';
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = scrollHeight + 'px';
      textarea.style.overflowY = 'hidden';
    }

    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 24;
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const oneLineThreshold = lineHeight + paddingTop + paddingBottom + 2;
    const hasWrappedLine = scrollHeight > oneLineThreshold;
    const hasExplicitNewLine = (textarea.value || '').includes('\n');
    setShowClearButton(hasWrappedLine || hasExplicitNewLine);
  };

  useEffect(() => {
    if (value && typeof value === 'string') {
      const words = value.trim().split(/\s+/).filter((w) => w.length > 0);
      setWordCount(words.length);
      setShowMicrophone(value.trim().length === 0);
    } else {
      setWordCount(0);
      setShowMicrophone(true);
    }
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      updateTextareaLayout(inputRef.current);
    }
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setIsVoiceText(false);
    const words = newValue.trim().split(/\s+/).filter((w) => w.length > 0);
    if (words.length > MAX_WORDS) {
      onChange(words.slice(0, MAX_WORDS).join(' '));
    } else {
      onChange(newValue);
    }
  };

  const handleVoiceDone = (text) => {
    if (!text || !text.trim()) return;
    const words = text.trim().split(/\s+/).filter((w) => w.length > 0);
    const limited = words.length > MAX_WORDS ? words.slice(0, MAX_WORDS).join(' ') : text.trim();
    onChange(limited);
    setIsVoiceText(true);
    setShowRecordingModal(false);
  };

  const handleOpenRecording = () => {
    if (disabled) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(t('freeTextSearch.voiceNotSupported'));
      return;
    }
    setShowRecordingModal(true);
  };

  const handleClear = () => {
    onChange('');
    setIsVoiceText(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSubmit = () => {
    if (!value || typeof value !== 'string' || value.trim().length === 0) return;
    if (onSubmit) {
      onSubmit(value.trim(), isVoiceText);
      setIsVoiceText(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFocus = () => { if (onFocus) onFocus(); };
  const handleBlur = () => {
    setTimeout(() => {
      const suggestionsContainer = document.querySelector('.prompt-suggestions-container');
      const isInteracting = suggestionsContainer?.getAttribute('data-interacting') === 'true';
      if (isInteracting) {
        const input = inputRef.current;
        if (input && document.activeElement !== input) input.focus();
        return;
      }
      if (onBlur) onBlur();
    }, 200);
  };

  return (
    <div className="free-text-input-container" style={{ position: 'relative', width: '100%' }}>
      <style>{`
        .free-text-input-container textarea::-webkit-scrollbar { width: 6px; }
        .free-text-input-container textarea::-webkit-scrollbar-track { background: transparent; margin: 4px 0; }
        .free-text-input-container textarea::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.3); border-radius: 3px; margin: 2px 0; }
        .free-text-input-container textarea::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.5); }
      `}</style>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => {
            handleInputChange(e);
            updateTextareaLayout(e.target);
          }}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={className}
          placeholder={t('freeTextSearch.placeholder')}
          style={{
            ...style,
            paddingRight: '50px',
            resize: 'none',
            overflowY: 'auto',
            minHeight: '50px',
            maxHeight: '80px',
            lineHeight: '1.5',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.3) transparent',
          }}
          disabled={disabled}
          rows={1}
        />
        {showClearButton && value && typeof value === 'string' && value.trim().length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '10px',
              top: '0',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              transition: 'color 0.2s',
              zIndex: 10,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'; }}
            title={t('freeTextSearch.clear') || 'Pulisci'}
          >
            <i className="bi bi-x" style={{ fontSize: '22px' }} />
          </button>
        )}
        <div style={{ position: 'absolute', right: '10px', bottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {showMicrophone && (
            <button
              type="button"
              onClick={handleOpenRecording}
              disabled={disabled}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                opacity: disabled ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
              title={t('freeTextSearch.recording')}
            >
              <i className="bi bi-mic" style={{ fontSize: '18px' }} />
            </button>
          )}
          {!showMicrophone && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={disabled || !value || typeof value !== 'string' || value.trim().length === 0}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: disabled || !value?.trim() ? 'not-allowed' : 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                opacity: disabled || !value?.trim() ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
              title={t('freeTextSearch.send')}
            >
              <i className="bi bi-send" style={{ fontSize: '18px' }} />
            </button>
          )}
        </div>
      </div>
      <div
        style={{
          marginTop: '6px',
          fontSize: '12px',
          color: wordCount >= MAX_WORDS ? '#ff4444' : wordCount > 0 ? '#888' : '#666',
          textAlign: 'right',
          paddingRight: '4px',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <span>{wordCount}/{MAX_WORDS} {t('freeTextSearch.wordsLabel')}</span>
      </div>
      <RecordingModal
        isOpen={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
        onDone={handleVoiceDone}
        maxRecordingSeconds={MAX_RECORDING_TIME}
      />
    </div>
  );
};

export default FreeTextInput;
